import Component from '@glimmer/component';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { on } from '@ember/modifier';
import { macroCondition, isDevelopingApp } from '@embroider/macros';
import { or, not } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import set from 'ember-set-helper/helpers/set';
import {
  HdsModal,
  HdsButtonSet,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import { Typography, ModalConfirm } from 'core/components';
import { boxPadding } from 'core/modifiers';
import {
  isAwsProviderNetwork,
  isAzureProviderNetwork,
} from 'networks-common/utils/networks';
import { isHvnToHvnPeering } from 'networks-common/utils/peerings';

/**
 *
 * `model` will be a model containing the peerings list, the current organization, and project.
 * @argument model;
 * @type {Object}
 *
 */
/**
 *
 * `peering` is the peering to delete.
 * @argument peering;
 * @type {Object}
 *
 */
/**
 *
 * `isOpen` is a boolean that controls whether the modal is shown or not
 * @argument isOpen;
 * @type {Boolean}
 *
 */
/**
 *
 * `openStateChanged` can optionally be provided when deletion request succeeds
 * @argument openStateChanged;
 * @type {Boolean}
 *
 */
/**
 *
 * `PageHvnPeeringsDeletePeeringModal` renders the Delete Peering Modal.
 *
 *
 * ```
 * <Page::HvnPeerings::List::DeletePeeringModal
 *   @model={{model}}
 *   @peering={{model.peering}}
 *   @isOpen={{this.showDeleteModal}}
 *   @onActiveChange={{set this 'showDeleteModal'}}
 * />
 * ```
 *
 * @class PageHvnPeeringsDeletePeeringModal
 *
 */
export default class PageHvnPeeringsDeletePeeringModal extends Component {
  @service api;
  @service operation;
  @service flashMessages;
  @service intl;
  @service analytics;

  get provider() {
    return this.args.model.network.location.region.provider;
  }

  get virtualSystemName() {
    let virtualSystemName;

    // HCP calls this HVN - HashiCorp Virtual Network
    if (isHvnToHvnPeering(this.args.peering)) {
      virtualSystemName = this.args.peering.target.hvnTarget.hvn.id;
    }
    // AWS calls this VPC - Virtual Private Cloud
    else if (isAwsProviderNetwork(this.args.model.network)) {
      virtualSystemName = this.args.peering.target.awsTarget.vpcId;
    }
    // Azure calls this VNet - Virtual Network
    else if (isAzureProviderNetwork(this.args.model.network)) {
      virtualSystemName = this.args.peering.target.azureTarget.vnetName;
    }
    return virtualSystemName;
  }

  get isHvnToHvnPeering() {
    return isHvnToHvnPeering(this.args.peering);
  }

  deletePeering = task(async (evt) => {
    evt.preventDefault();
    let { organization, project } = this.args.model;
    let { hvn, id } = this.args.peering;
    let operation;

    try {
      let resp = await this.api.network.deletePeering(
        organization.id,
        project.id,
        hvn.id,
        id
      );
      this.flashMessages.success(
        this.intl.t('components.page.hvn-peerings.delete.success.title')
      );
      this.analytics.trackEvent(
        'external_ui_hashicorp_network_peering_delete_modal_delete_button_clicked',
        this.args.peering
      );
      operation = resp.operation;
    } catch (e) {
      if (macroCondition(isDevelopingApp())) {
        console.error(e);
      }
      // TODO - make this a flash message
      throw e;
    }
    this.args.openStateChanged(false);
    this.operation.addOperation(operation);
  });

  <template>
    {{#if @isOpen}}
      <HdsModal
        @color="critical"
        @onClose={{@openStateChanged}}
        data-test-delete-peering-modal
        as |M|
      >
        <M.Header @icon="alert-triangle">
          {{t "components.page.hvn-peerings.list.delete-modal.title"}}
        </M.Header>
        <M.Body data-test-delete-peering-modal-body>
          <Typography
            @variant="body1"
            class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
            {{boxPadding "0 0 md 0"}}
          >
            {{#if this.isHvnToHvnPeering}}
              {{t
                "components.page.hvn-peerings.list.delete-modal.hvn-warning"
                virtualSystemName=this.virtualSystemName
                htmlSafe=true
              }}
            {{else}}
              {{t
                "components.page.hvn-peerings.list.delete-modal.warning"
                provider=this.provider
                virtualSystemName=this.virtualSystemName
                htmlSafe=true
              }}
            {{/if}}
          </Typography>
          <Typography
            @variant="body1"
            class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
            {{boxPadding "0 0 md 0"}}
          >
            {{#if this.isHvnToHvnPeering}}
              {{t
                "components.page.hvn-peerings.list.delete-modal.hvn-recreation"
              }}
            {{else}}
              {{t
                "components.page.hvn-peerings.list.delete-modal.recreation"
                provider=this.provider
              }}
            {{/if}}
          </Typography>
          <form
            id="delete"
            class="hcp-hds-form"
            {{on "submit" this.deletePeering.perform}}
          >
            <ModalConfirm @onAllowedChange={{set this "allowDelete"}} />
          </form>
        </M.Body>
        <M.Footer as |F|>
          <HdsButtonSet>
            <HdsButton
              @color="critical"
              @text={{t "components.form.delete"}}
              disabled={{or
                (not this.allowDelete)
                this.deletePeering.isRunning
              }}
              form="delete"
              type="submit"
              data-test-confirm-delete
            />
            <HdsButton
              @color="secondary"
              @text={{t "components.form.cancel"}}
              disabled={{this.deletePeering.isRunning}}
              {{on "click" F.close}}
            />
          </HdsButtonSet>
        </M.Footer>
      </HdsModal>
    {{/if}}
  </template>
}
